// PricingBlock — renders the customer-visible pricing display.
//
// Per v2_PRICING_API_CONTRACT.md §Reference: what the customer-visible
// PDF actually shows — per-line title/quantity/unit_price/line_price,
// one total_sale_amount, terms, conditions, sales rep, tax disclosure.
// NEVER a per-operation breakdown.
//
// When tier_applied.code === 'pending_shown_as_standard', renders the
// "contact sales to confirm your account" banner per v2_CUSTOMER_IDENTITY.md.

import { useTranslation } from 'react-i18next';

import { fieldKeyLabel, type PricingResponseDto } from 'shared';

import { formatCurrency, formatNumber } from '../i18n/formatters.js';
import { ARTWORK_PENDING_DISCLAIMER, customerFacingFailure } from '../pricingCopy.js';

type Props = {
  response: PricingResponseDto;
  // Terms / conditions / sales-rep / tax disclosure render only when
  // the customer has actually requested the quote be sent to sales
  // (status: submitted / pending_approval / approved). The pre-order
  // "See my price" view shows the line items + total only — the legal
  // boilerplate belongs to the submitted artifact, not the explorer.
  show_terms?: boolean;
  // "Quote now, art to follow" — when true, render the "subject to change upon
  // receipt of final artwork" disclaimer under the total. Shown regardless of
  // show_terms: it's precisely the pre-order explorer where an art-to-follow
  // customer needs to see the caveat.
  artwork_pending?: boolean;
};

function formatMoney(amount: number, currency: string): string {
  return formatCurrency(amount, currency, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

// A priced line's unit/line price is null when Nexus withheld it. In practice
// such a response never reaches this component — priceQuote collapses any
// withheld-price quote to the generic contact-sales failure before it can be
// rendered as a price — but the wire type is nullable, so render "—" defensively
// rather than "$NaN".
function money(amount: number | null, currency: string): string {
  return amount == null ? '—' : formatMoney(amount, currency);
}

export function PricingBlock({ response, show_terms = false, artwork_pending = false }: Props): JSX.Element {
  const { t } = useTranslation(['pricing', 'common']);

  if (!response.ok) {
    const f = response.failure;
    const copy = customerFacingFailure(f);
    // The fields Nexus named on a 422 INVALID_CONFIG (`details.field_keys`),
    // resolved to the labels the form shows.
    const rawFieldKeys = f.details?.field_keys;
    const failedFieldLabels = Array.isArray(rawFieldKeys)
      ? rawFieldKeys.filter((k): k is string => typeof k === 'string').map(fieldKeyLabel)
      : [];
    return (
      <div className="banner banner--error">
        <strong>{copy.headline}</strong>
        <p style={{ margin: '8px 0 0' }}>{copy.body}</p>
        {copy.show_contact_sales && (
          <p style={{ margin: '8px 0 0' }}>
            {/* The same person the body just named — Nexus resolves the account's
                rep and rides it on failures too, so the CTA is never a shared
                inbox for a partner who has one (FB-0026). */}
            <a href={`mailto:${copy.sales_rep.email}`}>
              {t('block.failure.emailLink', { name: copy.sales_rep.name })}
            </a>
          </p>
        )}
        {failedFieldLabels.length > 0 && (
          // Nexus named the offending fields. Render their CUSTOMER labels, not
          // the raw attribute keys the wire carries — a customer reading
          // "cover_finish" cannot find the control, and the same failure is
          // listed as "Lamination" in the near-button summary that owns the
          // repair path (QuoteEdit.applyFieldKeyFailure). One vocabulary, both
          // lists (FB-0051).
          <p style={{ margin: '8px 0 0' }}>
            {t('block.failure.fieldsNeedingAttention', {
              fields: failedFieldLabels.join(', '),
            })}
          </p>
        )}
        {copy.reference_id && (
          <p style={{ margin: '8px 0 0', fontSize: '0.875em', opacity: 0.8 }}>
            {t('block.failure.reference', { id: copy.reference_id })}
          </p>
        )}
      </div>
    );
  }

  const p = response.pricing;
  const showPendingBanner = p.tier_applied.code === 'pending_shown_as_standard';

  return (
    <div className="card pricing-block">
      <h2>{t('block.title')}</h2>
      {showPendingBanner && p.tier_applied.message && (
        <div className="banner banner--warn">
          <strong>{p.tier_applied.message}</strong>
        </div>
      )}
      <table>
        <thead>
          <tr>
            <th>{t('block.table.item')}</th>
            <th style={{ textAlign: 'right' }}>{t('block.table.quantity')}</th>
            <th style={{ textAlign: 'right' }}>{t('block.table.perUnit')}</th>
            <th style={{ textAlign: 'right' }}>{t('block.table.subtotal')}</th>
          </tr>
        </thead>
        <tbody>
          {p.lines.map((l) => (
            <tr key={l.draft_line_id}>
              <td>{l.title}</td>
              <td style={{ textAlign: 'right' }}>{formatNumber(l.quantity)}</td>
              <td style={{ textAlign: 'right' }}>{money(l.unit_price, p.currency)}</td>
              <td style={{ textAlign: 'right' }}>{money(l.line_price, p.currency)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={3} className="total">
              {t('block.table.total')}
            </td>
            <td className="total">{formatMoney(p.total_sale_amount, p.currency)}</td>
          </tr>
        </tfoot>
      </table>
      {artwork_pending && (
        // "Quote now, art to follow" — Nexus owns this wording (mirrored in
        // pricingCopy). Shown under the total so the caveat sits with the price.
        <p className="help" style={{ marginTop: 12, fontStyle: 'italic' }}>
          {ARTWORK_PENDING_DISCLAIMER}
        </p>
      )}
      {show_terms && (
        <>
          <p style={{ marginTop: 16 }}>
            <strong>{t('block.terms.label')}</strong> {p.terms}
          </p>
          {p.sales_rep && (
            <p>
              <strong>{t('block.salesRep.label')}</strong> {p.sales_rep.name} ({p.sales_rep.email})
            </p>
          )}
          <div className="legal">
            <p>{p.conditions_text}</p>
            {p.tax_disclosure && <p>{p.tax_disclosure}</p>}
          </div>
        </>
      )}
    </div>
  );
}
